import React, {useCallback, useState} from "react";
import { Dropdown, DropdownButton } from "react-bootstrap";
import {inject, observer} from "mobx-react";
import {useLocation, useNavigate} from "react-router";
import CopyToClipboard from "react-copy-to-clipboard";
import * as PropTypes from "prop-types";
import {getTheme, toggleTheme, THEMES} from "../../lib/themeManager";
import {getConfig} from "../../lib/configMgr";

const KEY_API = "api";
const KEY_SIGNOUT = "sign-out";
const KEY_CHANGEPASS = "change-password";
const KEY_THEME = "theme";
const KEY_API_DOCS = "api-docs";
const KEY_DEV_DOCS = "dev-docs";

/**
 * NavBarIconLabel
 * Stacks a glyphicon above a small ALL-CAPS text label.
 */
const NavBarIconLabel = ({ icon, label }) => (
  <span className="navbar-icon-label">
    <span className={'glyphicon ' + icon} aria-hidden="true" />
    <span className="navbar-icon-label__text">{label}</span>
  </span>
);

const UserMenu = ({
  userName,
  userGroups,
  isAdmin,
  showAdmin,
  apiKey,
  className,
  currentTheme,
  onSignOut,
  onChangePassword,
  onGenerateAPIKey,
  onToggleTheme,
  onOpenAPIDocs,
}) => {
  const [copied, setCopied] = useState(false);

  const handleCopy = () => {
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  return (
  <span title="User information" className="user-menu">
    <DropdownButton
      id="dropdown-user-menu"
      key="user"
      className={className}
      onSelect={(key) => {
        if (key === KEY_API) {
          onGenerateAPIKey();
        } else if (key === KEY_DEV_DOCS) {
          window.open("/developer", "_blank", "noopener,noreferrer");
        } else if (key === KEY_API_DOCS) {
          onOpenAPIDocs();
        } else if (key === KEY_SIGNOUT) {
          onSignOut();
        } else if (key === KEY_CHANGEPASS) {
          onChangePassword();
        } else if (key === KEY_THEME) {
          onToggleTheme();
        }
      }}
      title={<NavBarIconLabel icon="glyphicon-user" label="Profile" />}
    >
      <Dropdown.Header>
        Hello, {userName ? userName : "anonymous"}!
      </Dropdown.Header>
      {isAdmin && userGroups.length > 0 && (
        <Dropdown.Header>
          Member of {JSON.stringify(userGroups)}
        </Dropdown.Header>
      )}
      <Dropdown.Divider />
      <Dropdown.Item eventKey={KEY_THEME}>
        {currentTheme === THEMES.DARK ? "☀️ Light Mode" : "🌙 Dark Mode"}
      </Dropdown.Item>
      <Dropdown.Divider />
      <Dropdown.Header>API Key</Dropdown.Header>
      <CopyToClipboard text={apiKey || ""} onCopy={handleCopy}>
        <Dropdown.Item
          title="Current API Key, click to copy"
          className={"api-key"}
          disabled={!apiKey}
        >
          <span className="glyphicon glyphicon-copy" />
          <span className={"api-key"}>
            {apiKey ? apiKey : "No key has been generated"}
          </span>
          {copied && <span className="api-key-copied">Copied!</span>}
        </Dropdown.Item>
      </CopyToClipboard>
      <Dropdown.Item title="Generate a new API Key" eventKey={KEY_API}>
        {apiKey ? "Regenerate" : "Generate"} API Key
      </Dropdown.Item>
      <Dropdown.Divider />
      <Dropdown.Item eventKey={KEY_DEV_DOCS}>Developer Docs</Dropdown.Item>
      <Dropdown.Item
        eventKey={KEY_API_DOCS}
        disabled={!apiKey}
      >API Reference</Dropdown.Item>
      <Dropdown.Divider />
      <Dropdown.Item eventKey={KEY_CHANGEPASS}>Change Password</Dropdown.Item>
      <Dropdown.Divider />
      <Dropdown.Item eventKey={KEY_SIGNOUT}>Sign Out</Dropdown.Item>
    </DropdownButton>
  </span>
  );
};


UserMenu.propTypes = {
  userName: PropTypes.string,
  currentTheme: PropTypes.string,
  onChangePassword: PropTypes.func,
  onSignOut: PropTypes.func,
  onToggleTheme: PropTypes.func
};
const UserMenuWrapper = ({
    userStore,
    searchSessionStore,
    appStatusStore,
    className,
  }) => {

  const {userName, userGroups, apiKey, isAdmin, showAdmin} = userStore;
  const navigate = useNavigate();
  const location = useLocation();

  const [currentTheme, setCurrentTheme] = React.useState(getTheme());

  const handleChangePassword = useCallback(() => {
    navigate("/changepass", {
      state: {
        username: userName
      }
    });
  }, [userName]);

  const handleSignOut = useCallback(() => {
    const wasSSOUser = userStore.isSSOUser;
    appStatusStore.clear();
    searchSessionStore.clear();
    userStore.logout();
    if (wasSSOUser) {
      const { AWSCognito: { authDomain, clientID } } = getConfig();
      const logoutUri = `${window.location.origin}/`;
      window.location.href = `https://${authDomain}/logout?client_id=${clientID}&logout_uri=${encodeURIComponent(logoutUri)}`;
    } else {
      window.location.href = '/signin';
    }
  }, [appStatusStore, searchSessionStore, userStore]);

  const handleGenerateAPIKey = useCallback(() => {
    userStore.generateAPIKey();
  }, [userStore]);

  const handleOpenAPIDocs = useCallback(() => {
    userStore.refreshUICookie();
    const params = new URLSearchParams();
    if (currentTheme === "dark") params.set("theme", "dark");
    const qs = params.toString();
    window.open(`/api/ui/${qs ? "?" + qs : ""}`, "_blank", "noopener,noreferrer");
  }, [userStore, currentTheme]);

  const handleToggleTheme = useCallback(() => {
    toggleTheme();
    setCurrentTheme(getTheme());
  }, []);

  return (
      <UserMenu
          className={className}
          userName={userName}
          isAdmin={isAdmin}
          userGroups={userGroups}
          apiKey={apiKey}
          currentTheme={currentTheme}
          showAdmin={showAdmin}
          onChangePassword={handleChangePassword}
          onSignOut={handleSignOut}
          onGenerateAPIKey={handleGenerateAPIKey}
          onToggleTheme={handleToggleTheme}
          onOpenAPIDocs={handleOpenAPIDocs}
      />
  );
};

export default inject("userStore", "appStatusStore", "searchSessionStore")(observer(UserMenuWrapper));

